import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { BulkActionKind, BulkActionStatus } from 'types/BulkActionTypes';
import { bulkExportDownload } from './functions/bulkExportDownload';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';

/*
 * BulkActionProgress.tsx
 * Shows the running progress bar of a bulk action and, once finished, its
 * result: a download button for an export or a generic result summary for
 * delete/deactivate/custom actions.
 */

interface BulkActionProgressProps {
    kind: BulkActionKind;
    status: BulkActionStatus | null;
    progress: number;
    totalRows: number;
    processedRows: number;
    resultSummary: Record<string, any> | null;
    resultFileUrl: string | null;
    errorMessage: string | null;
    onClose: () => void;
}

const BulkActionProgress: React.FC<BulkActionProgressProps> = ({
    kind, status, progress, totalRows, processedRows, resultSummary, resultFileUrl, errorMessage, onClose
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const isRunning = status === 'in_progress' || status === null;
    const isFinished = status === 'completed' || status === 'completed_with_errors';

    // Translate a frontend error key, show a raw backend error message as is
    const renderErrorMessage = () => {
        if (!errorMessage) {
            return t('bulk_actions.general.progress_failed_message');
        }
        if (errorMessage.startsWith('bulk_actions.')) {
            return t(errorMessage);
        }
        return `${t('bulk_actions.general.progress_failed_message')} ${errorMessage}`;
    };

    return (
        <div className='bulk-action-progress'>
            {isRunning && (
                // Show the running progress bar and the processed versus total rows
                <>
                    <h4>{t('bulk_actions.general.progress_in_progress_header')}</h4>
                    <p className='progress-message'>
                        {t('bulk_actions.general.progress_processing_label', {
                            progress,
                            processedRows: processedRows.toLocaleString(userLocale),
                            totalRows: totalRows.toLocaleString(userLocale),
                        })}
                    </p>
                    <div className='progress-bar'>
                        <div className='progress-bar-fill' style={{ width: `${progress}%` }}></div>
                    </div>
                </>
            )}
            {isFinished && (
                // Show the finished header, either fully or partially successful
                <>
                    <h4>
                        {status === 'completed'
                            ? t('bulk_actions.general.progress_completed_header')
                            : t('bulk_actions.general.progress_completed_with_errors_header')}
                    </h4>
                    <div className='progress-bar'>
                        <div className='progress-bar-fill' style={{ width: `${progress}%` }}></div>
                    </div>
                    {kind === 'export' && resultFileUrl && (
                        // Offer the direct download of the export result file
                        <PrimaryButton
                            onClick={() => bulkExportDownload(resultFileUrl)}
                            label='bulk_actions.general.download_export_button_label'
                            icon={<FontAwesomeIcon icon={faDownload} />}
                            size='small'
                        />
                    )}
                    {resultSummary && !(kind === 'export' && resultFileUrl) && (
                        // Show every entry of the result summary as a readable line
                        <ul className='result-summary-list'>
                            {Object.entries(resultSummary).map(([key, value]) => (
                                <li key={key}>
                                    {t(`bulk_actions.general.result_summary_labels.${key}`, { defaultValue: key })}: {typeof value === 'number' ? value.toLocaleString(userLocale) : String(value)}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
            {status === 'failed' && (
                // Show the error message reported by the backend
                <>
                    <h4>{t('bulk_actions.general.progress_failed_header')}</h4>
                    <p>{renderErrorMessage()}</p>
                </>
            )}
            {!isRunning && (
                // Only allow closing once the bulk action reached a final status
                <div className='button-row'>
                    <SecondaryButton
                        onClick={onClose}
                        label='general.close'
                        size='small'
                    />
                </div>
            )}
        </div>
    );
};

export default BulkActionProgress;
